import mixpanel, {Dict} from "mixpanel-browser";
import {useCallback} from "react";
import {PressEvent} from "react-aria-components";

/**
 * Given an `onClick` or `onPress` and some `props`, create a `trackedOnClick`
 * and `trackedOnPress` which safely wraps the original click handler in a way
 * that sends data to Mixpanel for analytics.
 *
 * `onPress` is the name used by `react-aria-components` for touch events across
 * platforms, hence the support for both.
 *
 * Example:
 * ```ts
 * // ...
 * const {trackedOnClick} = useButtonInputTracking({onClick, props});
 *
 * return <button onClick={trackedOnClick}>...</button>;
 * ```
 */
export function useButtonInputTracking({
	onClick,
	onPress,
	props,
}: UseButtonInputTrackingProps) {
	const trackInput = useCallback(
		() => mixpanel.track("button_click", dropFunctions(props)),
		[props]
	);

	const trackedOnClick = useCallback(
		(event: ReactMouseClickEvent) => {
			onClick?.(event);
			trackInput();
		},
		[onClick, trackInput]
	);

	const trackedOnPress = useCallback(
		(event: PressEvent) => {
			onPress?.(event);
			trackInput();
		},
		[onPress, trackInput]
	);

	return {trackedOnClick, trackedOnPress};
}

function dropFunctions<T extends Dict>(obj: T) {
	const result: Record<string, NonFunction<unknown>> = {};
	for (const [key, value] of Object.entries(obj)) {
		if (typeof value !== "function") {
			result[key] = value;
		}
	}
	return result;
}

interface InputTrackingBase {
	readonly props: Dict;
}

type ReactMouseClickEvent = React.MouseEvent<HTMLElement, MouseEvent>;

interface ClickTrackingProps extends InputTrackingBase {
	readonly onClick?: (e: ReactMouseClickEvent) => void;
	readonly onPress?: undefined;
}

interface PressTrackingProps extends InputTrackingBase {
	readonly onPress?: (e: PressEvent) => void;
	readonly onClick?: undefined;
}

type UseButtonInputTrackingProps = ClickTrackingProps | PressTrackingProps;

type NonFunction<T> = T extends Function ? never : T;
